// src/pages/ToursPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Calendar, Users, ArrowRight, Plus, Trash2 } from "lucide-react";
import { TopBar, RoadBadge, EmptyState, ErrorMsg } from "../components/UI";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
  ready:      { bg: "rgba(74,112,82,0.12)",  color: "#4A7052" },
  processing: { bg: "rgba(122,106,85,0.12)", color: "#7A6A55" },
  error:      { bg: "rgba(107,39,55,0.12)",  color: "#886070" },
  // A legitimate "cannot build a tour under the provided parameters"
  // solver result (see plan/handler.py's status: "infeasible") — same
  // color family as error since no tour exists, but distinct label so it
  // doesn't read as a bug/failure.
  infeasible: { bg: "rgba(107,39,55,0.12)",  color: "#886070" },
};

// Raw DynamoDB status values aren't traveler-facing copy — map to a short
// human label for the badge.
const STATUS_LABEL = {
  ready: "ready",
  processing: "processing",
  error: "error",
  infeasible: "cannot build",
};

function stopLabel(stop) {
  if (typeof stop === "string") return stop;
  return stop?.winery_name || stop?.name || "";
}

function TourItem({ tour, onClick, onDelete }) {
  const prefs = tour.preferences || {};
  const s = STATUS_STYLE[tour.status] || STATUS_STYLE.processing;
  const stops = tour.tour?.optimal_route || tour.tour?.days?.flatMap(d => d.stops || []) || [];
  const stopLabels = stops.map(stopLabel).filter(Boolean);
  return (
    <div className="card-hover w-full text-left" style={{ position: "relative" }}>
      {/* Grouped top-right action column: delete above, click affordance below — kept as a single
          aligned unit so they read as one control cluster instead of two icons competing for the corner. */}
      <div style={{ position: "absolute", top: 12, right: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, zIndex: 1 }}>
        <button onClick={onDelete} aria-label="Remove from My Tours" title="Remove from My Tours" style={{
          background: "none", border: "none", color: "#A8907E", cursor: "pointer", padding: 4, lineHeight: 0,
        }}>
          <Trash2 size={14} />
        </button>
        <ArrowRight size={16} className="text-stone" style={{ pointerEvents: "none" }} />
      </div>
      <button onClick={onClick} style={{ all: "unset", display: "block", width: "100%", cursor: "pointer", paddingRight: 32 }}>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <RoadBadge slug={prefs.road_slug} />
            <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 100 }}>
              {STATUS_LABEL[tour.status] || tour.status}
            </span>
          </div>
          <p className="font-display font-semibold text-slate-dark">
            {prefs.days || "?"}-day tour
          </p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-stone">
            <span className="flex items-center gap-1">
              <Calendar size={11} /> {prefs.days} {prefs.days === 1 ? "day" : "days"}
            </span>
            <span className="flex items-center gap-1">
              <Users size={11} /> {prefs.party_size} {prefs.party_size === 1 ? "person" : "people"}
            </span>
          </div>
        </div>

        {stopLabels.length > 0 && (
          <div className="mt-3 pt-3 border-t border-chalk-warm">
            <div className="flex gap-1.5 flex-wrap">
              {stopLabels.slice(0, 3).map((label, i) => (
                <span key={i} className="text-xs bg-chalk-warm text-slate px-2 py-0.5 rounded-full">
                  {label}
                </span>
              ))}
              {stopLabels.length > 3 && (
                <span className="text-xs text-stone px-1">+{stopLabels.length - 3} more</span>
              )}
            </div>
          </div>
        )}
      </button>
    </div>
  );
}

export default function ToursPage() {
  const { user }   = useAuth("traveler");
  const navigate   = useNavigate();
  const [tours,   setTours]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!user || user.role !== "traveler") { setLoading(false); return; }
    api.myTours()
      .then(setTours)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [user]);

  async function handleDelete(planId) {
    setTours(t => t.filter(x => x.plan_id !== planId)); // optimistic
    try { await api.deleteTour(planId); }
    catch (err) { setError(err.message); }
  }

  const signedIn = user && user.role === "traveler";

  return (
    <div className="min-h-screen pb-20">
      <TopBar
        title="My tours"
        right={
          <button onClick={() => navigate("/plan")}
            className="btn-primary py-1.5 px-3 text-xs gap-1">
            <Plus size={13} /> New
          </button>
        }
      />

      <div className="px-4 pt-6">
        {!signedIn && (
          <div className="card text-center py-8">
            <p className="font-display text-lg font-semibold text-slate-dark mb-1">
              Sign in to save tours
            </p>
            <p className="text-sm text-stone mb-4">
              Guest tours expire after a couple of hours (24h if you gave an email). Create a free account to keep them until you delete them.
            </p>
            <button onClick={() => navigate("/tourist/login", { state: { from: "/tours" } })} className="btn-primary">
              Sign in or register
            </button>
          </div>
        )}

        {signedIn && loading && (
          <div className="flex flex-col gap-3">
            {[1,2,3].map(i => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-chalk-warm rounded w-1/3 mb-3" />
                <div className="h-5 bg-chalk-warm rounded w-1/2 mb-2" />
                <div className="h-3 bg-chalk-warm rounded w-1/4" />
              </div>
            ))}
          </div>
        )}

        {signedIn && !loading && error && <ErrorMsg message={error} />}

        {signedIn && !loading && !error && tours.length === 0 && (
          <EmptyState
            icon={BookOpen}
            title="No tours yet"
            body="Plan your first wine route tour and it'll be saved here automatically."
            action={
              <button onClick={() => navigate("/plan")} className="btn-primary">
                Plan a tour
              </button>
            }
          />
        )}

        {signedIn && !loading && tours.length > 0 && (
          <div className="flex flex-col gap-3">
            {tours.map(tour => (
              <TourItem
                key={tour.plan_id}
                tour={tour}
                onClick={() => navigate(`/results?plan=${tour.plan_id}`)}
                onDelete={() => handleDelete(tour.plan_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
